import { Box, Chip, MenuItem, Select, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useMemo, useState } from 'react';
import { AiFillCalendar } from 'react-icons/ai';
import { GiLeg } from 'react-icons/gi';
import { TbSoccerField } from 'react-icons/tb';

import { Context } from '../../service/Context';
import theme from '../../styles/theme';
import { demarcaciones, lateralidades } from '../../utils/UtilesComun';
import { obtenerCategoriaEstimada } from '../../utils/UtilesJugador';
import BotonEdadCheck from '../botones/BotonEdadCheck';

/**
 * Separador vertical sutil entre controles
 */
const SeparadorVertical = () => (
  <Box
    sx={{
      borderLeft: '1px solid #cbd5e1',
      height: 24,
      mx: 1.25,
      display: { xs: 'none', sm: 'block' },
    }}
  />
);

/**
 * Contenedor estilizado para cada selector de datos
 */
const SelectorBox = ({ icon, children, sx = {} }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      height: 32,
      px: 1,
      backgroundColor: '#ffffff',
      border: '1px solid #cbd5e1',
      borderRadius: '6px',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
      transition: 'all 0.15s ease-in-out',
      '&:hover': {
        borderColor: theme.palette.primary.main,
      },
      ...sx,
    }}
  >
    {icon && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: theme.palette.primary.main,
          fontSize: '1.05rem',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
    )}
    {children}
  </Box>
);

SelectorBox.propTypes = {
  icon: PropTypes.node,
  children: PropTypes.node,
  sx: PropTypes.object,
};

const selectCommonStyle = {
  fontSize: '0.80rem',
  fontWeight: 600,
  backgroundColor: 'transparent',
  border: 'none',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiSelect-select': {
    py: 0,
    pl: 0.5,
    pr: '18px !important',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  '& .MuiSelect-icon': {
    color: '#64748b',
    fontSize: '1rem',
    right: 0,
  },
  '&:before, &:after': {
    display: 'none !important',
  },
  '& fieldset': {
    display: 'none',
  },
};

const menuProps = {
  PaperProps: {
    sx: {
      maxHeight: 260,
      borderRadius: '6px',
      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
      '& .MuiMenuItem-root': { fontSize: '0.80rem', py: 0.5 },
    },
  },
};

/**
 * Datos de un jugador en formato de barra horizontal (metadatos modernos).
 */
const DatosJugador = ({ datosJugador, onDatosChange, temporadaActual }) => {
  const { modificarJugador } = useContext(Context);

  const [anoSeleccionado, setAnoSeleccionado] = useState(datosJugador?.ano || '');
  const [demarcacionSeleccionada, setDemarcacionSeleccionada] = useState(datosJugador?.demarcacion || '');
  const [lateralidadSeleccionada, setLateralidadSeleccionada] = useState(datosJugador?.lateralidad || '');

  const currentYear = new Date().getFullYear();
  const anos = useMemo(() => [...Array(51).keys()].map((i) => currentYear - i).reverse(), [currentYear]);

  // Categoría estimada para la temporada actual según el año de nacimiento
  const categoriaEstimada = useMemo(() => {
    return obtenerCategoriaEstimada(anoSeleccionado, temporadaActual);
  }, [anoSeleccionado, temporadaActual]);

  if (!datosJugador) {
    return null;
  }

  const clickAnoNacimiento = (idJugador, ano) => {
    modificarJugador(idJugador, { ano });
    if (onDatosChange) {
      onDatosChange({ ano });
    }
  };

  const clickDemarcacion = (idJugador, demarcacion) => {
    modificarJugador(idJugador, { demarcacion });
    if (onDatosChange) {
      onDatosChange({ demarcacion });
    }
  };

  const clickLateralidad = (idJugador, lateralidad) => {
    modificarJugador(idJugador, { lateralidad });
    if (onDatosChange) {
      onDatosChange({ lateralidad });
    }
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, alignItems: 'center' }}>
      {/* Año de nacimiento + Verificación */}
      <SelectorBox icon={<AiFillCalendar />}>
        <Select
          value={anoSeleccionado || ''}
          onChange={(e) => {
            const nuevoAno = e.target.value;
            setAnoSeleccionado(nuevoAno);
            clickAnoNacimiento(datosJugador.idJugador, nuevoAno);
          }}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) => (
            <span style={{ color: selected ? '#1e293b' : '#64748b' }}>{selected || 'Año'}</span>
          )}
          sx={{
            ...selectCommonStyle,
            minWidth: 48,
          }}
          MenuProps={menuProps}
        >
          <MenuItem value="" disabled sx={{ fontSize: '0.80rem' }}>
            Año
          </MenuItem>
          {anos.map((ano) => (
            <MenuItem key={ano} value={ano} sx={{ fontSize: '0.80rem' }}>
              {ano}
            </MenuItem>
          ))}
        </Select>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 0.25 }}>
          <BotonEdadCheck
            idJugador={datosJugador.idJugador}
            checkInicial={datosJugador.anoCheck}
            enLista={true}
            onChange={(nuevoCheck) => {
              if (onDatosChange) {
                onDatosChange({ anoCheck: nuevoCheck });
              }
            }}
          />
        </Box>
      </SelectorBox>

      {/* Categoría Estimada (misma sección que el año) */}
      {categoriaEstimada && (
        <Tooltip title={`Categoría estimada en la temporada ${temporadaActual || 'actual'}`} arrow>
          <Chip
            label={categoriaEstimada.nombre}
            size="small"
            sx={{
              height: 32,
              px: 1,
              fontSize: '0.78rem',
              fontWeight: 600,
              backgroundColor: '#f8fafc',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        </Tooltip>
      )}

      {/* Demarcación / Posición */}
      <SeparadorVertical />
      <SelectorBox icon={<TbSoccerField />}>
        <Select
          value={demarcacionSeleccionada || ''}
          onChange={(e) => {
            const nuevaDemarcacion = e.target.value;
            setDemarcacionSeleccionada(nuevaDemarcacion);
            clickDemarcacion(datosJugador.idJugador, nuevaDemarcacion);
          }}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) => (
            <span style={{ color: selected ? '#1e293b' : '#64748b' }}>{selected || 'Posición'}</span>
          )}
          sx={{
            ...selectCommonStyle,
            minWidth: 70,
          }}
          MenuProps={menuProps}
        >
          <MenuItem value="" disabled sx={{ fontSize: '0.80rem' }}>
            Posición
          </MenuItem>
          {demarcaciones.map((dem) => (
            <MenuItem key={dem} value={dem} sx={{ fontSize: '0.80rem' }}>
              {dem}
            </MenuItem>
          ))}
        </Select>
      </SelectorBox>

      {/* Lateralidad */}
      <SeparadorVertical />
      <SelectorBox icon={<GiLeg />}>
        <Select
          value={lateralidadSeleccionada || ''}
          onChange={(e) => {
            const nuevaLateralidad = e.target.value;
            setLateralidadSeleccionada(nuevaLateralidad);
            clickLateralidad(datosJugador.idJugador, nuevaLateralidad);
          }}
          displayEmpty
          variant="standard"
          disableUnderline
          renderValue={(selected) => (
            <span style={{ color: selected ? '#1e293b' : '#64748b' }}>{selected || 'Pierna'}</span>
          )}
          sx={{
            ...selectCommonStyle,
            minWidth: 55,
          }}
          MenuProps={menuProps}
        >
          <MenuItem value="" disabled sx={{ fontSize: '0.80rem' }}>
            Pierna
          </MenuItem>
          {lateralidades.map((lat) => (
            <MenuItem key={lat} value={lat} sx={{ fontSize: '0.80rem' }}>
              {lat}
            </MenuItem>
          ))}
        </Select>
      </SelectorBox>
    </Box>
  );
};

DatosJugador.propTypes = {
  datosJugador: PropTypes.shape({
    idJugador: PropTypes.number,
    nombre: PropTypes.string,
    ano: PropTypes.number,
    demarcacion: PropTypes.string,
    lateralidad: PropTypes.string,
    anoCheck: PropTypes.bool,
  }),
  onDatosChange: PropTypes.func,
  temporadaActual: PropTypes.string,
};

export default DatosJugador;
